// OnboardingContext - Manages prospect onboarding state
// UPDATED: Jan 2026 - Added isArchived state for soft archive gating
import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { STAGES, canTransitionTo, STAGE_INFO } from '../config/stages'
import { n8nApi } from '../services/n8nApi'
import { useAuth } from './AuthContext'
import { useAdmin } from './AdminContext'
import { useLocation, useNavigate } from 'react-router-dom'

const OnboardingContext = createContext({})

// Mock data for demo mode - realistic sample data for previewing onboarding steps
const DEMO_MOCK_DATA = {
  fullName: 'Alex Thompson',
  companyName: 'Thompson Haulage Ltd',
  email: 'alex@thompsonhaulage.co.uk',
  phone: '+44 7700 900123',
  demoPreference: 'personalised',
  companyAddress: '45 Fleet Street, Manchester, M1 2AB',
  consentStatus: 'signed',
  consentSignedAt: new Date().toISOString(),
  docusignEnvelopeId: 'demo-envelope-123',
  samsaraOrgId: 'org_demo_12345',
  samsaraRegion: 'eu',
  samsaraConnected: true,
  demoStatus: 'pending',
  demoUrl: null,
  demoExpiresAt: null,
  introMeetingScheduled: true,
  introMeetingScheduledAt: new Date(Date.now() + 86400000).toISOString(), // Tomorrow
  introCalendlyEventUuid: 'demo-intro-uuid',
  introCalendlyRescheduleUrl: null,
  introCalendlyCancelUrl: null,
  meetingScheduled: true,
  meetingScheduledAt: new Date(Date.now() + 172800000).toISOString(), // Day after tomorrow
  calendlyEventUuid: 'demo-meeting-uuid',
  calendlyRescheduleUrl: null,
  calendlyCancelUrl: null,
  provisioned: false,
}

export const useOnboarding = () => {
  const context = useContext(OnboardingContext)
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider')
  }
  return context
}

// Initial prospect data structure
// Updated: Added intro meeting fields for two-meeting flow
const initialProspectData = {
  // Contact information (Stage 02)
  fullName: '',
  companyName: '',
  email: '',
  phone: '',
  
  // Demo preference (now set by admin, not user)
  demoPreference: null, // 'personalised' | 'generic' | null
  
  // Company address for contract (Stage 04)
  companyAddress: '',
  
  // Consent status
  consentStatus: 'pending', // 'pending' | 'signed' | 'declined'
  consentSignedAt: null,
  docusignEnvelopeId: null,
  
  // Dashcam provider API details (Stage 06) — field names kept for backend compatibility
  samsaraOrgId: '',
  samsaraRegion: 'us', // 'us' | 'eu'
  samsaraConnected: false,
  
  // Demo status (legacy)
  demoStatus: 'pending',
  demoUrl: null,
  demoExpiresAt: null,

  // Intro meeting scheduling (Stage 02 → 03)
  introMeetingScheduled: false,
  introMeetingScheduledAt: null,
  introCalendlyEventUuid: null,
  introCalendlyRescheduleUrl: null,
  introCalendlyCancelUrl: null,

  // Personalised demo meeting scheduling (Stage 07 → 10)
  meetingScheduled: false,
  meetingScheduledAt: null,
  calendlyEventUuid: null,
  calendlyRescheduleUrl: null,
  calendlyCancelUrl: null,

  //Provisioning status (NEW Jan 7, 2026)
  provisioned: false

}

export const OnboardingProvider = ({ children }) => {
  const { user } = useAuth()
  const { impersonating, isImpersonating, demoMode, isDemoMode } = useAdmin()
  const location = useLocation()
  const navigate = useNavigate()
  const [currentStage, setCurrentStage] = useState(STAGES.INTEREST_CONFIRMED)
  const [prospectData, setProspectData] = useState(initialProspectData)
  const [stageHistory, setStageHistory] = useState([
    { stage: STAGES.INTEREST_CONFIRMED, timestamp: new Date().toISOString() }
  ])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isSynced, setIsSynced] = useState(false)
  
  // Archive state - when true, user sees "account inactive" message
  const [isArchived, setIsArchived] = useState(false)

  // Determine which UID to use for data operations
  // If impersonating, use the prospect's UID; otherwise use the logged-in user's UID
  const effectiveUid = impersonating?.uid || user?.uid

  // Track the admin's UID for audit purposes when impersonating
  const actingUid = isImpersonating ? user?.uid : null

  // Track if we've already handled the justCreatedRecord state for this navigation
  const handledJustCreated = useRef(false)
  // Track the previous pathname to detect actual navigation (not just re-renders)
  const prevPathname = useRef(location.pathname)
  // Track if a fetch is currently in progress to prevent concurrent calls
  const fetchInProgress = useRef(false)

  // Fetch user data from n8n/Airtable when user logs in OR when impersonation changes
  useEffect(() => {
    // Detect if this is a new navigation (pathname changed)
    const isNewNavigation = prevPathname.current !== location.pathname
    if (isNewNavigation) {
      console.log(`📍 Navigation: ${prevPathname.current} → ${location.pathname}`)
      prevPathname.current = location.pathname
      handledJustCreated.current = false // Reset for new navigation
    }

    // Check if we just created a record
    const justCreatedRecord = location.state?.justCreatedRecord === true

    // IMPORTANT: Don't proceed with justCreatedRecord handling until we have effectiveUid
    // This prevents the race condition where we set handledJustCreated but can't actually fetch
    if (justCreatedRecord && !handledJustCreated.current) {
      if (!effectiveUid) {
        // User auth still loading - wait for next effect run when effectiveUid is available
        console.log('⏳ Waiting for user auth before handling justCreatedRecord...')
        setIsLoading(true)
        return
      }

      console.log('🔄 Just created record, resetting sync state...')
      handledJustCreated.current = true
      setIsSynced(false)
      setIsLoading(true)

      // Clear the navigation state to prevent re-processing on subsequent renders
      // Use setTimeout to avoid state update during render
      setTimeout(() => {
        navigate(location.pathname, { replace: true, state: {} })
      }, 0)
    }

    const fetchUserData = async () => {
      // DEMO MODE: Use mock data instead of fetching from Airtable
      if (isDemoMode && demoMode?.stage) {
        console.log('🎭 Demo mode active - using mock data for stage:', demoMode.stage)
        setCurrentStage(demoMode.stage)
        setProspectData(DEMO_MOCK_DATA)
        setIsArchived(false)
        setError(null)
        setIsSynced(true)
        setIsLoading(false)
        return
      }

      if (!effectiveUid) {
        setIsSynced(false)
        setIsArchived(false)
        return
      }

      // Skip fetch if user is on complete-profile page (no Airtable record yet)
      if (location.pathname === '/complete-profile') {
        console.log('⏭️ Skipping fetch on /complete-profile - no record yet')
        setIsLoading(false)
        setIsSynced(true)  // Mark as synced so page renders
        return
      }

      // Prevent concurrent fetch calls
      if (fetchInProgress.current) {
        console.log('⏭️ Fetch already in progress, skipping...')
        return
      }
      fetchInProgress.current = true

      setIsLoading(true)
      setError(null)  // Clear any previous errors

      try {
        let response

        // If we just created a record, poll until Airtable has it (with timeout)
        if (justCreatedRecord && handledJustCreated.current) {
          console.log('⏳ Polling for newly created Airtable record...')
          const maxAttempts = 15  // Increased from 10 to give more time
          const delayMs = 1000

          for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            // Wait before each attempt
            await new Promise(resolve => setTimeout(resolve, delayMs))

            try {
              response = await n8nApi.getUserStatus(effectiveUid, { acting: actingUid })
              if (response.found && response.prospect) {
                console.log(`✅ Record found on attempt ${attempt}`)
                break
              }
            } catch (err) {
              console.warn(`Attempt ${attempt} failed:`, err.message)
            }

            if (attempt === maxAttempts) {
              console.error('❌ Record not found after max attempts')
              throw new Error('Your account is still being set up. Please refresh the page in a moment.')
            }

            console.log(`⏳ Record not ready, retrying... (${attempt}/${maxAttempts})`)
          }
        } else {
          // Normal fetch (not a just-created record)
          // If this is a brand new session, give Airtable a moment to sync
          if (!isSynced && !isImpersonating) {
            console.log('⏳ New session, waiting 1s for Airtable...')
            await new Promise(resolve => setTimeout(resolve, 1000))
          }

          response = await n8nApi.getUserStatus(effectiveUid, { acting: actingUid })
        }

        if (response.found && response.prospect) {
          // User exists in Airtable - load their data
          const prospect = response.prospect

          // Check if user is archived (but allow admins impersonating to still see)
          const userIsArchived = prospect.archived === true
          setIsArchived(userIsArchived && !isImpersonating)

          setCurrentStage(prospect.onboarding_stage)
          setProspectData({
            fullName: prospect.full_name || '',
            companyName: prospect.company_name || '',
            email: prospect.email || '',
            phone: prospect.phone || '',
            demoPreference: prospect.demo_preference || null,
            companyAddress: prospect.company_address || '',
            consentStatus: prospect.consent_status || 'pending',
            consentSignedAt: prospect.consent_signed_at || null,
            docusignEnvelopeId: prospect.docusign_envelope_id || null,
            samsaraOrgId: prospect.samsara_org_id || '',
            samsaraRegion: prospect.samsara_region || 'us',
            samsaraConnected: prospect.samsara_connected || false,
            demoStatus: prospect.demo_status || 'pending',
            demoUrl: prospect.demo_url || null,
            demoExpiresAt: prospect.demo_expires_at || null,
            // Intro meeting fields
            introMeetingScheduled: prospect.intro_meeting_scheduled || false,
            introMeetingScheduledAt: prospect.intro_meeting_scheduled_at || null,
            introCalendlyEventUuid: prospect.intro_calendly_event_uuid || null,
            introCalendlyRescheduleUrl: prospect.intro_calendly_reschedule_url || null,
            introCalendlyCancelUrl: prospect.intro_calendly_cancel_url || null,
            // Personalised demo meeting fields
            meetingScheduled: prospect.meeting_scheduled || false,
            meetingScheduledAt: prospect.meeting_scheduled_at || null,
            calendlyEventUuid: prospect.calendly_event_uuid || null,
            calendlyRescheduleUrl: prospect.calendly_reschedule_url || null,
            calendlyCancelUrl: prospect.calendly_cancel_url || null,
            //Provisioning status (NEW Jan 7, 2026)
            provisioned: prospect.provisioned || false
          })

          if (isImpersonating) {
            console.log('🎭 Loaded prospect data for impersonation:', prospect.full_name, userIsArchived ? '(ARCHIVED)' : '')
          } else {
            console.log('✅ User data loaded from Airtable', userIsArchived ? '(ARCHIVED)' : '')
            console.log('DEBUG prospect:', prospect.provisioned, prospect.archived)
          }
          setIsSynced(true)
        } else {
          // User doesn't exist in Airtable yet
          console.warn('⚠️ User exists in Firebase but not in Airtable.')

          // Set basic data
          setProspectData(prev => ({
            ...prev,
            email: impersonating?.email || user?.email || ''
          }))

          setIsArchived(false)
          setIsSynced(true)

          if (!isImpersonating) {
            setError('Your account is being set up. Please refresh the page in a moment.')
          } else {
            setError('Prospect data not found in Airtable.')
          }
        }
      } catch (err) {
        console.error('❌ Error fetching user data:', err)
        setError('Failed to load your onboarding status. Please check your connection and try again.')
        setIsSynced(true) // Mark as synced so we exit loading state and can show error
      } finally {
        setIsLoading(false)
        fetchInProgress.current = false
      }
    }

    fetchUserData()
  }, [effectiveUid, isImpersonating, isDemoMode, demoMode?.stage, location.pathname, location.state, navigate]) // Re-fetch when effective UID, impersonation, demo mode, or navigation state changes

  // Manual refetch function for retry after errors
  const refetchUserData = useCallback(async () => {
    if (!effectiveUid) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await n8nApi.getUserStatus(effectiveUid, {
        acting: actingUid
      })

      if (response.found && response.prospect) {
        const prospect = response.prospect
        const userIsArchived = prospect.archived === true
        setIsArchived(userIsArchived && !isImpersonating)

        setCurrentStage(prospect.onboarding_stage)
        setProspectData({
          fullName: prospect.full_name || '',
          companyName: prospect.company_name || '',
          email: prospect.email || '',
          phone: prospect.phone || '',
          demoPreference: prospect.demo_preference || null,
          companyAddress: prospect.company_address || '',
          consentStatus: prospect.consent_status || 'pending',
          consentSignedAt: prospect.consent_signed_at || null,
          docusignEnvelopeId: prospect.docusign_envelope_id || null,
          samsaraOrgId: prospect.samsara_org_id || '',
          samsaraRegion: prospect.samsara_region || 'us',
          samsaraConnected: prospect.samsara_connected || false,
          demoStatus: prospect.demo_status || 'pending',
          demoUrl: prospect.demo_url || null,
          demoExpiresAt: prospect.demo_expires_at || null,
          introMeetingScheduled: prospect.intro_meeting_scheduled || false,
          introMeetingScheduledAt: prospect.intro_meeting_scheduled_at || null,
          introCalendlyEventUuid: prospect.intro_calendly_event_uuid || null,
          introCalendlyRescheduleUrl: prospect.intro_calendly_reschedule_url || null,
          introCalendlyCancelUrl: prospect.intro_calendly_cancel_url || null,
          meetingScheduled: prospect.meeting_scheduled || false,
          meetingScheduledAt: prospect.meeting_scheduled_at || null,
          calendlyEventUuid: prospect.calendly_event_uuid || null,
          calendlyRescheduleUrl: prospect.calendly_reschedule_url || null,
          calendlyCancelUrl: prospect.calendly_cancel_url || null,
          provisioned: prospect.provisioned || false
        })

        console.log('✅ User data reloaded successfully')
        setIsSynced(true)
      } else {
        setError('Your account is being set up. Please try again in a moment.')
      }
    } catch (err) {
      console.error('❌ Error refetching user data:', err)
      setError('Failed to load your onboarding status. Please check your connection and try again.')
    } finally {
      setIsLoading(false)
    }
  }, [effectiveUid, actingUid, isImpersonating])

  // Reset state when impersonation ends
  useEffect(() => {
    if (!isImpersonating && user?.uid) {
      // When we stop impersonating, re-fetch the admin's own data
      // The effectiveUid change will trigger the above useEffect
      setIsSynced(false)
      setIsArchived(false)
    }
  }, [isImpersonating, user?.uid])

  // Update prospect data
  const updateProspectData = useCallback((updates) => {
    setProspectData(prev => ({
      ...prev,
      ...updates
    }))
  }, [])

  // Sync state from server response
  // Use this when polling detects a backend state change (e.g., DocuSign callback)
  const syncFromServer = useCallback(async (serverProspect) => {
    if (!serverProspect) return false
    
    console.log('🔄 Syncing state from server:', serverProspect.onboarding_stage)
    
    // Update stage
    if (serverProspect.onboarding_stage) {
      setCurrentStage(serverProspect.onboarding_stage)
      setStageHistory(prev => [
        ...prev,
        { 
          stage: serverProspect.onboarding_stage, 
          timestamp: new Date().toISOString(),
          source: 'server_sync'
        }
      ])
    }
    
    // Update prospect data
    setProspectData(prev => ({
      ...prev,
      consentStatus: serverProspect.consent_status || prev.consentStatus,
      consentSignedAt: serverProspect.consent_signed_at || prev.consentSignedAt,
      samsaraConnected: serverProspect.samsara_connected || prev.samsaraConnected,
      samsaraOrgId: serverProspect.samsara_org_id || prev.samsaraOrgId,
      samsaraRegion: serverProspect.samsara_region || prev.samsaraRegion,
      demoStatus: serverProspect.demo_status || prev.demoStatus,
      demoUrl: serverProspect.demo_url || prev.demoUrl,
      // Intro meeting fields
      introMeetingScheduled: serverProspect.intro_meeting_scheduled ?? prev.introMeetingScheduled,
      introMeetingScheduledAt: serverProspect.intro_meeting_scheduled_at || prev.introMeetingScheduledAt,
      introCalendlyRescheduleUrl: serverProspect.intro_calendly_reschedule_url || prev.introCalendlyRescheduleUrl,
      introCalendlyCancelUrl: serverProspect.intro_calendly_cancel_url || prev.introCalendlyCancelUrl,
      // Demo meeting fields
      meetingScheduled: serverProspect.meeting_scheduled ?? prev.meetingScheduled,
      meetingScheduledAt: serverProspect.meeting_scheduled_at || prev.meetingScheduledAt,
      calendlyRescheduleUrl: serverProspect.calendly_reschedule_url || prev.calendlyRescheduleUrl,
      calendlyCancelUrl: serverProspect.calendly_cancel_url || prev.calendlyCancelUrl,
      //Provisioning status (NEW 7 Jan, 2026)
      provisioned: serverProspect.provisioned ?? prev.provisioned
    }))
    
    return true
  }, [])

  // Transition to a new stage
  const transitionToStage = useCallback(async (targetStage, additionalData = {}) => {
    // Validate the transition
    if (!canTransitionTo(currentStage, targetStage)) {
      const error = `Invalid stage transition: ${currentStage} → ${targetStage}`
      setError(error)
      console.error(error)
      return false
    }

    setIsLoading(true)
    setError(null)

    try {
      // For now, just update local state
      // In future, you can add n8n calls here to update Airtable directly
      // For most stage transitions, the n8n webhooks will handle the updates
      // when forms are submitted (interest form, consent, API setup, etc.)
      
      setCurrentStage(targetStage)
      setStageHistory(prev => [
        ...prev,
        { 
          stage: targetStage, 
          timestamp: new Date().toISOString(),
          fromStage: currentStage
        }
      ])

      // Update any additional data passed with the transition
      if (Object.keys(additionalData).length > 0) {
        updateProspectData(additionalData)
      }

      return true
    } catch (err) {
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [currentStage, updateProspectData])

  // Get current stage info
  const getCurrentStageInfo = useCallback(() => {
    return STAGE_INFO[currentStage] || null
  }, [currentStage])

  // Get available next stages
  const getAvailableTransitions = useCallback(() => {
    const transitions = []
    for (const [stage, info] of Object.entries(STAGE_INFO)) {
      if (canTransitionTo(currentStage, stage)) {
        transitions.push({ stage, ...info })
      }
    }
    return transitions
  }, [currentStage])

  // Reset onboarding (for testing/development)
  const resetOnboarding = useCallback(() => {
    setCurrentStage(STAGES.INTEREST_CONFIRMED)
    setProspectData(initialProspectData)
    setStageHistory([
      { stage: STAGES.INTEREST_CONFIRMED, timestamp: new Date().toISOString() }
    ])
    setError(null)
    setIsArchived(false)
  }, [])

  // Clear error
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const value = {
    // State
    currentStage,
    prospectData,
    stageHistory,
    isLoading,
    error,
    isSynced,
    isArchived, // NEW: Archive status for gating
    
    // Impersonation/Demo mode (for components that need to know)
    effectiveUid,
    isImpersonating,
    actingUid,
    isDemoMode,
    
    // Actions
    updateProspectData,
    transitionToStage,
    syncFromServer,
    resetOnboarding,
    clearError,
    refetchUserData,
    
    // Helpers
    getCurrentStageInfo,
    getAvailableTransitions,
    
    // Stage constants for convenience
    STAGES
  }

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  )
}
